import { useTranslation } from 'react-i18next';
import { Plus, Minus, ArrowRight } from 'lucide-react';
import { Button } from '@ooh/ui/components/button';
import { ModalDialog } from '@ooh/ui/components/modal-dialog';
import { offerVersionEndpoints } from '@ooh/contracts';
import type { OfferVersion, OfferVersionDiffRow } from '@ooh/contracts';
import { formatDisplayDate } from '@ooh/ui/lib/date-format';
import { useApiSuspenseQuery } from '../../data/use-api-suspense-query';
import { QueryBoundary } from '../../layouts/query-boundary';
import {
  DiffSection,
  DiffSummary,
  AddedRemovedGroup,
  ChangedGroup,
} from './version-diff-dialog-sections';

// ─── Version meta label ─────────────────────────────────────────
// Only used by the fetching container below — VersionDiffDialogView takes the
// already-formatted label as a plain string so it stays decoupled from the
// full OfferVersion shape (see the mockup's local equivalent for why that
// decoupling matters: apps/mockups keeps a structurally different, looser
// OfferVersion mock type).

function useVersionMetaLabel(version: OfferVersion): string {
  const { t } = useTranslation('offers');
  const date = formatDisplayDate(version.publishedAt);
  const author = version.publishedByName
    ? t('workspace.versions.publishedByName', { name: version.publishedByName })
    : t('workspace.versions.publishedBy', { id: version.publishedBy });
  return `${t('workspace.versions.versionBadge', { number: version.versionNumber })} (${date}, ${author})`;
}

// ─── Dialog body (loading / error / empty / grouped rows) ──────

function DiffDialogBody({
  rows,
  isLoading,
  isError,
  priorNetPrice,
  currentNetPrice,
  onNavigateToUnit,
}: {
  rows: OfferVersionDiffRow[];
  isLoading: boolean;
  isError: boolean;
  priorNetPrice: number | null;
  currentNetPrice: number | null;
  onNavigateToUnit?: (unitId: string) => void;
}) {
  const { t } = useTranslation('offers');

  if (isLoading) {
    return (
      <div
        className="py-6 text-center text-sm text-muted-foreground"
        data-testid="diff-dialog-loading"
      >
        {t('workspace.versions.diff.loading')}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="py-6 text-center text-sm text-destructive" data-testid="diff-dialog-error">
        {t('workspace.versions.diff.error')}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div
        className="py-6 text-center text-sm text-muted-foreground"
        data-testid="diff-dialog-empty"
      >
        {t('workspace.versions.diff.empty')}
      </div>
    );
  }

  const added = rows.filter((row) => row.changeType === 'added');
  const removed = rows.filter((row) => row.changeType === 'removed');
  const changed = rows.filter((row) => row.changeType === 'changed');

  return (
    <div className="flex flex-col gap-5">
      <DiffSummary
        addedCount={added.length}
        removedCount={removed.length}
        changedCount={changed.length}
        priorNetPrice={priorNetPrice}
        currentNetPrice={currentNetPrice}
      />

      {added.length > 0 ? (
        <DiffSection
          title={t('workspace.versions.diff.section.addedTitle', { count: added.length })}
          tone="emerald"
          icon={<Plus className="h-3 w-3" strokeWidth={3} />}
        >
          <AddedRemovedGroup
            rows={added}
            lineThrough={false}
            tone="emerald"
            onNavigateToUnit={onNavigateToUnit}
          />
        </DiffSection>
      ) : null}

      {removed.length > 0 ? (
        <DiffSection
          title={t('workspace.versions.diff.section.removedTitle', { count: removed.length })}
          tone="red"
          icon={<Minus className="h-3 w-3" strokeWidth={3} />}
        >
          <AddedRemovedGroup
            rows={removed}
            lineThrough
            tone="red"
            onNavigateToUnit={onNavigateToUnit}
          />
        </DiffSection>
      ) : null}

      {changed.length > 0 ? (
        <DiffSection
          title={t('workspace.versions.diff.section.changedTitle', { count: changed.length })}
          tone="amber"
          icon={<ArrowRight className="h-3 w-3" />}
        >
          <ChangedGroup rows={changed} onNavigateToUnit={onNavigateToUnit} />
        </DiffSection>
      ) : null}
    </div>
  );
}

// ─── Dialog header/footer ───────────────────────────────────────

function DiffDialogHeader({
  currentVersionLabel,
  priorVersionLabel,
}: {
  currentVersionLabel: string;
  priorVersionLabel: string;
}) {
  const { t } = useTranslation('offers');

  return (
    <div>
      <h2 className="text-base font-semibold">{t('workspace.versions.diff.dialogTitle')}</h2>
      <p className="mt-0.5 text-xs text-muted-foreground">
        {t('workspace.versions.diff.dialogSubtitle', {
          from: priorVersionLabel,
          to: currentVersionLabel,
        })}
      </p>
    </div>
  );
}

function DiffDialogFooter({
  changeDescription,
  onClose,
}: {
  changeDescription: string | null | undefined;
  onClose: () => void;
}) {
  const { t } = useTranslation('offers');

  return (
    <>
      {changeDescription ? (
        <p className="mr-auto text-xs text-muted-foreground">{changeDescription}</p>
      ) : null}
      <Button variant="outline" onClick={onClose}>
        {t('workspace.versions.diff.closeBtn')}
      </Button>
    </>
  );
}

// ─── VersionDiffDialogView (presentational — no fetching, no OfferVersion) ──
// Extracted so apps/mockups can render the exact same grouped-comparison UI
// as apps/web without duplicating DiffSection/DiffSummary/formatDiffUnitLabel
// (see apps/mockups/.../offer-workspace/version-diff-dialog.mockup.tsx, which
// imports this component instead of reimplementing it). Deliberately takes
// pre-formatted version labels and net prices instead of full OfferVersion
// objects so it stays usable from a caller with a structurally different
// (mock) version shape.

const DIALOG_MAX_WIDTH = 'max-w-2xl';

export interface VersionDiffDialogViewProps {
  currentVersionLabel: string;
  priorVersionLabel: string;
  changeDescription: string | null | undefined;
  rows: OfferVersionDiffRow[];
  isLoading: boolean;
  isError: boolean;
  priorNetPrice: number | null;
  currentNetPrice: number | null;
  /** Called when a unit row is activated; omit to render rows as non-interactive. */
  onNavigateToUnit?: (unitId: string) => void;
  onClose: () => void;
}

export function VersionDiffDialogView({
  currentVersionLabel,
  priorVersionLabel,
  changeDescription,
  rows,
  isLoading,
  isError,
  priorNetPrice,
  currentNetPrice,
  onNavigateToUnit,
  onClose,
}: VersionDiffDialogViewProps) {
  const { t } = useTranslation('offers');

  return (
    <ModalDialog
      onClose={onClose}
      maxWidth={DIALOG_MAX_WIDTH}
      testId="version-diff-dialog"
      closeLabel={t('workspace.versions.diff.closeAriaLabel')}
      header={
        <DiffDialogHeader
          currentVersionLabel={currentVersionLabel}
          priorVersionLabel={priorVersionLabel}
        />
      }
      footer={<DiffDialogFooter changeDescription={changeDescription} onClose={onClose} />}
    >
      <DiffDialogBody
        rows={rows}
        isLoading={isLoading}
        isError={isError}
        priorNetPrice={priorNetPrice}
        currentNetPrice={currentNetPrice}
        onNavigateToUnit={onNavigateToUnit}
      />
    </ModalDialog>
  );
}

// ─── VersionDiffDialog (fetching container) ─────────────────

export interface VersionDiffDialogProps {
  offerId: string;
  currentVersion: OfferVersion;
  priorVersion: OfferVersion;
  onClose: () => void;
}

// Opens the unit detail in a new tab rather than navigating in place: the diff
// dialog is a comparison surface a user typically walks through unit by unit,
// so a same-tab navigation would tear down the dialog after the first click.
function openUnitInNewTab(unitId: string) {
  window.open(`/media-units/${unitId}`, '_blank', 'noopener,noreferrer');
}

type DiffViewProps = Omit<VersionDiffDialogViewProps, 'rows' | 'isLoading' | 'isError'>;

function VersionDiffDialogLoaded({
  offerId,
  currentVersion,
  priorVersion,
  viewProps,
}: Omit<VersionDiffDialogProps, 'onClose'> & { viewProps: DiffViewProps }) {
  const { data } = useApiSuspenseQuery(offerVersionEndpoints.diff, {
    id: Number(offerId),
    versionId: currentVersion.id,
    against: priorVersion.id,
  });

  return (
    <VersionDiffDialogView {...viewProps} rows={data.rows} isLoading={false} isError={false} />
  );
}

export function VersionDiffDialog({
  offerId,
  currentVersion,
  priorVersion,
  onClose,
}: VersionDiffDialogProps) {
  const currentVersionLabel = useVersionMetaLabel(currentVersion);
  const priorVersionLabel = useVersionMetaLabel(priorVersion);

  const viewProps: DiffViewProps = {
    currentVersionLabel,
    priorVersionLabel,
    changeDescription: currentVersion.changeDescription,
    priorNetPrice: priorVersion.totalNetPrice,
    currentNetPrice: currentVersion.totalNetPrice,
    onNavigateToUnit: openUnitInNewTab,
    onClose,
  };
  const placeholder = { ...viewProps, rows: [] };

  return (
    <QueryBoundary
      fallback={<VersionDiffDialogView {...placeholder} isLoading isError={false} />}
      renderError={() => <VersionDiffDialogView {...placeholder} isLoading={false} isError />}
    >
      <VersionDiffDialogLoaded
        offerId={offerId}
        currentVersion={currentVersion}
        priorVersion={priorVersion}
        viewProps={viewProps}
      />
    </QueryBoundary>
  );
}
